import React, { useCallback, useMemo, useState, useRef, useEffect } from "react";
import Dropdown, { DropdownOption } from "../../../components/common/Dropdown";
import MultiCheckboxSelect from "../../../components/common/MultiCheckboxSelect";
import ErrorBanner, {
  ValidationError,
} from "../../../components/common/ErrorBanner";
import { searchByOptions, UBT_ID_SEARCH_BY_VALUE } from "../../../store/slices/selectUBTSlice";

type Props = {
  regionOptions: DropdownOption[];
  entityOptions: DropdownOption[];
  areaOptions: DropdownOption[];
  centerOptions: DropdownOption[];
  locationOptions: DropdownOption[];
  costCenterOptions: DropdownOption[];
  selectedRegionCode: DropdownOption;
  selectedAreaCode: DropdownOption;
  selectedCenterCode: DropdownOption;
  selectedEntityCode: DropdownOption;
  selectedLocationCode: DropdownOption;
  selectedSearchBy: DropdownOption;
  selectedSearchByText: string;
  selectedFromId: string;
  selectedToId: string;
  selectedCostCenterCode: {
    isAllSelected: boolean;
    selectedIds: (string | number)[];
  };
  sortByCostCenter: string;
  onRegionChange: (opt: DropdownOption | null) => void;
  onAreaChange: (
    opt: DropdownOption | null,
    regionCode: string,
    sortBy: string,
  ) => void;
  onEntityChange: (opt: DropdownOption | null) => void;
  onCenterChange: (
    opt: DropdownOption | null,
    regionCode: string,
    areaCode: string,
    sortBy: string,
  ) => void;
  onLocationChange: (
    opt: DropdownOption | null,
    regionCode: string,
    areaCode: string,
    centerCode: string,
    sortBy: string,
  ) => void;
  onCostCenterChange: (
    isAllSelected: boolean,
    selectedIds: (string | number)[],
  ) => void;
  onCostCenterSortBy: (
    locationCode: string,
    regionCode: string,
    areaCode: string,
    centerCode: string,
    sortBy: string,
  ) => void;
  onSearch: (params: Record<string, unknown>) => void;
  onSearchByTextChange: (text: string) => void;
  onSearchByChange: (opt: DropdownOption) => void;
  onFromIdChange: (value: string) => void;
  onToIdChange: (value: string) => void;
  isLoadingData: boolean;
};

const defaultOption: DropdownOption = {
  id: 0,
  label: "All",
  value: "All",
};

const defaultRegionOption: DropdownOption = {
  id: "",
  label: "Select",
  value: "",
};

const MAX_SAFE_INTEGER_VALUE = "9007199254740991";

const isWithinMaxSafeInteger = (value: string): boolean => {
  const normalizedValue = value.replace(/^0+(?=\d)/, "");

  return (
    normalizedValue.length < MAX_SAFE_INTEGER_VALUE.length ||
    (normalizedValue.length === MAX_SAFE_INTEGER_VALUE.length &&
      normalizedValue <= MAX_SAFE_INTEGER_VALUE)
  );
};

// Reusable validation error display component
const ValidationErrorDisplay: React.FC<{ message: string; id: string }> = ({
  message,
  id,
}) => (
  <div className="error-description">
    <ul id={id}>
      <li>
        <img
          src="/assets/images/errorCircleSmall.svg"
          alt="Error icon"
          width="14"
          height="14"
        />
        <span className="screenreader-only">{message}</span> {message}
      </li>
    </ul>
  </div>
);

// Helper to add valid dropdown params (filters out empty/placeholder/"All" values)
const addDropdownParam = (
  params: Record<string, unknown>,
  dropdown: DropdownOption | undefined,
  key: string,
) => {
  if (dropdown?.value !== undefined && dropdown.value !== "") {
    params[key] = dropdown.value;
  }
};

const validateRegion = (
  region: DropdownOption,
  fieldRef: React.RefObject<HTMLElement>,
): ValidationError[] =>
  region.value === "" || region.value === "Select"
    ? [
        {
          field: "region",
          message: "Region is required. Please select a region.",
          fieldRef,
        },
      ]
    : [];

const validateSearchText = (
  searchText: string,
  isUbtIdSearch: boolean,
  fieldRef: React.RefObject<HTMLElement>,
): ValidationError[] => {
  const trimmedSearch = searchText.trim();
  if (!trimmedSearch) return [];

  if (!isUbtIdSearch && trimmedSearch.length < 3) {
    return [
      {
        field: "searchText",
        message: "Search text must contain at least 3 alpha-numeric characters.",
        fieldRef,
      },
    ];
  }

  if (isUbtIdSearch && !/^[0-9%$]+$/.test(trimmedSearch)) {
    return [
      {
        field: "searchText",
        message: "Search value should be a non-negative number.",
        fieldRef,
      },
    ];
  }

  if (isUbtIdSearch) {
    const numericSearchValue = trimmedSearch.replaceAll(/[%$]/g, "");
    if (
      numericSearchValue &&
      !isWithinMaxSafeInteger(numericSearchValue)
    ) {
      return [
        {
          field: "searchText",
          message: `Search value must be less than or equal to ${MAX_SAFE_INTEGER_VALUE}.`,
          fieldRef,
        },
      ];
    }
  }

  return [];
};

const validateUbtIdRange = (
  fromIdValue: string,
  toIdValue: string,
  fieldRef: React.RefObject<HTMLElement>,
): ValidationError[] => {
  const fromId = fromIdValue.trim();
  const toId = toIdValue.trim();
  const isNonNegativeInt = (value: string) => /^\d+$/.test(value);
  const errors: ValidationError[] = [];

  if (fromId && !isNonNegativeInt(fromId)) {
    errors.push({
      field: "fromId",
      message: "From ID must be a non-negative whole number.",
      fieldRef,
    });
  }
  if (fromId && isNonNegativeInt(fromId) && !isWithinMaxSafeInteger(fromId)) {
    errors.push({
      field: "fromId",
      message: `From ID must be less than or equal to ${MAX_SAFE_INTEGER_VALUE}.`,
      fieldRef,
    });
  }
  if (toId && !isNonNegativeInt(toId)) {
    errors.push({
      field: "toId",
      message: "To ID must be a non-negative whole number.",
      fieldRef,
    });
  }
  if (toId && isNonNegativeInt(toId) && !isWithinMaxSafeInteger(toId)) {
    errors.push({
      field: "toId",
      message: `To ID must be less than or equal to ${MAX_SAFE_INTEGER_VALUE}.`,
      fieldRef,
    });
  }
  if (
    fromId &&
    toId &&
    isNonNegativeInt(fromId) &&
    isNonNegativeInt(toId) &&
    Number(fromId) > Number(toId)
  ) {
    errors.push({
      field: "idRange",
      message: "From ID must be less than or equal to To ID.",
      fieldRef,
    });
  }

  return errors;
};

const validateCostCenterSelection = (
  isAllSelected: boolean,
  selectedIds: (string | number)[],
  fieldRef: React.RefObject<HTMLElement>,
): ValidationError[] =>
  !isAllSelected && selectedIds.length > 40
    ? [
        {
          field: "costCenter",
          message: `The max number of cost center selections is 40. Your current selection contains ${selectedIds.length}.`,
          fieldRef,
        },
      ]
    : [];

const UBTSearchContainer: React.FC<Props> = React.memo(
  ({
    regionOptions,
    entityOptions,
    areaOptions,
    centerOptions,
    locationOptions,
    costCenterOptions,
    onRegionChange,
    onAreaChange,
    onCenterChange,
    onLocationChange,
    onCostCenterChange,
    onSearch,
    onCostCenterSortBy,
    onEntityChange,
    onSearchByChange,
    onSearchByTextChange,
    onFromIdChange,
    onToIdChange,
    selectedRegionCode = defaultRegionOption,
    selectedAreaCode = defaultOption,
    selectedCenterCode = defaultOption,
    selectedEntityCode = defaultOption,
    selectedLocationCode = defaultOption,
    selectedCostCenterCode = { isAllSelected: true, selectedIds: [] },
    sortByCostCenter = "id",
    isLoadingData = false,
    selectedSearchByText = "",
    selectedSearchBy = searchByOptions[0],
    selectedFromId = "",
    selectedToId = "",
  }) => {
    const [validationErrors, setValidationErrors] = useState<ValidationError[]>(
      [],
    );
    const [hasAttemptedSearch, setHasAttemptedSearch] = useState(false);

    // Refs for error navigation
    const regionRef = useRef<HTMLDivElement>(null);
    const searchInputRef = useRef<HTMLInputElement>(null);
    const fromIdRef = useRef<HTMLInputElement>(null);
    const costCenterRef = useRef<HTMLDivElement>(null);

    const isUbtIdSearch = selectedSearchBy?.value === UBT_ID_SEARCH_BY_VALUE;
    const hasLoadedCostCenterOptions =
      costCenterOptions.length > 0 &&
      costCenterOptions[0]?.value !== defaultOption.value;
    const effectiveCostCenterIds = useMemo(() => {
      if (selectedCostCenterCode.isAllSelected && hasLoadedCostCenterOptions) {
        return costCenterOptions.map((option) => option.id);
      }
      return selectedCostCenterCode.selectedIds;
    }, [costCenterOptions, hasLoadedCostCenterOptions, selectedCostCenterCode]);

    const validateForm = useCallback((): ValidationError[] => {
      const errors = validateRegion(
        selectedRegionCode,
        regionRef as React.RefObject<HTMLElement>,
      );
      errors.push(
        ...validateSearchText(
          selectedSearchByText,
          isUbtIdSearch,
          searchInputRef as React.RefObject<HTMLElement>,
        ),
      );
      if (isUbtIdSearch) {
        errors.push(
          ...validateUbtIdRange(
            selectedFromId,
            selectedToId,
            fromIdRef as React.RefObject<HTMLElement>,
          ),
        );
      }
      errors.push(
        ...validateCostCenterSelection(
          selectedCostCenterCode.isAllSelected,
          selectedCostCenterCode.selectedIds,
          costCenterRef as React.RefObject<HTMLElement>,
        ),
      );
      return errors;
    }, [
      selectedRegionCode.value,
      selectedSearchByText,
      selectedCostCenterCode,
      isUbtIdSearch,
      selectedFromId,
      selectedToId,
    ]);

    // Real-time validation after search attempt
    useEffect(() => {
      if (hasAttemptedSearch) {
        const errors = validateForm();
        !errors.length && setHasAttemptedSearch(false); // Reset after validation
        setValidationErrors(errors);
      }
    }, [
      hasAttemptedSearch,
      validateForm,
      selectedRegionCode.value,
      selectedSearchByText,
      selectedCostCenterCode.isAllSelected,
      selectedCostCenterCode.selectedIds.length,
      selectedFromId,
      selectedToId,
    ]);

    const handleSearchBy = useCallback(
      (opt: DropdownOption) => {
        onSearchByChange(opt);
      },
      [onSearchByChange],
    );
    const handleSearchByTextChange = useCallback(
      (text: string) => {
        // When UBT ID# is selected, only digits and the % wildcard are valid.
        // Strip all other characters at the input level so the user cannot type letters.
        onSearchByTextChange(text);
      },
      [onSearchByTextChange],
    );
    const handleFromIdChange = useCallback(
      (value: string) => {
        onFromIdChange(value);
      },
      [onFromIdChange],
    );
    const handleToIdChange = useCallback(
      (value: string) => {
        onToIdChange(value);
      },
      [onToIdChange],
    );

    const handleRegion = useCallback(
      (opt: DropdownOption) => {
        onRegionChange(opt);
      },
      [onRegionChange],
    );

    const handleArea = useCallback(
      (opt: DropdownOption) => {
        if (selectedRegionCode?.value) {
          onAreaChange(opt, selectedRegionCode.value, sortByCostCenter);
        }
      },
      [onAreaChange, selectedRegionCode.value, sortByCostCenter],
    );
    const handleEntity = useCallback(
      (opt: DropdownOption) => {
        if (selectedRegionCode?.value) {
          onEntityChange(opt);
        }
      },
      [onEntityChange, selectedRegionCode],
    );

    const handleCenter = useCallback(
      (opt: DropdownOption) => {
        if (selectedRegionCode?.value && selectedAreaCode?.value) {
          onCenterChange(
            opt,
            selectedRegionCode.value,
            selectedAreaCode.value,
            sortByCostCenter,
          );
        }
      },
      [
        onCenterChange,
        selectedRegionCode.value,
        selectedAreaCode.value,
        sortByCostCenter,
      ],
    );

    const handleLocation = useCallback(
      (opt: DropdownOption) => {
        if (
          selectedRegionCode?.value &&
          selectedAreaCode?.value &&
          selectedCenterCode?.value
        ) {
          onLocationChange(
            opt,
            selectedRegionCode.value,
            selectedAreaCode.value,
            selectedCenterCode.value,
            sortByCostCenter,
          );
        }
      },
      [
        onLocationChange,
        selectedRegionCode.value,
        selectedAreaCode.value,
        selectedCenterCode.value,
        sortByCostCenter,
      ],
    );

    const handleCostCenterChange = useCallback(
      (selectedIds: (number | string)[], isAllSelected: boolean) => {
        onCostCenterChange(isAllSelected, selectedIds);
      },
      [onCostCenterChange],
    );

    const handleCostCenterSortBy = useCallback(
      (v: string) => {
        const regionCode = selectedRegionCode?.value;
        const areaCode = selectedAreaCode?.value;
        const centerCode = selectedCenterCode?.value;
        const locationCode = selectedLocationCode?.value;
        onCostCenterSortBy(locationCode, regionCode, areaCode, centerCode, v);
      },
      [
        selectedRegionCode.value,
        selectedAreaCode.value,
        selectedCenterCode.value,
        selectedLocationCode.value,
        onCostCenterSortBy,
      ],
    );

    const buildSearchParams = useCallback(() => {
      const params: Record<string, unknown> = {};

      addDropdownParam(params, selectedSearchBy, "searchBy");

      if (selectedSearchByText?.trim()) {
        params.searchText = selectedSearchByText.trim();
      }

      // From/To ID range only applies to UBT ID# search (BFF rejects it otherwise).
      if (selectedSearchBy?.value === UBT_ID_SEARCH_BY_VALUE) {
        if (selectedFromId?.trim()) {
          params.fromId = selectedFromId.trim();
        }
        if (selectedToId?.trim()) {
          params.toId = selectedToId.trim();
        }
      }

      addDropdownParam(params, selectedRegionCode, "regionCode");
      addDropdownParam(params, selectedEntityCode, "entityCode");
      addDropdownParam(params, selectedAreaCode, "areaCode");
      addDropdownParam(params, selectedCenterCode, "centerCode");
      addDropdownParam(params, selectedLocationCode, "locationCode");

      if (selectedCostCenterCode.selectedIds.length > 0) {
        params.costCenterIds = selectedCostCenterCode.isAllSelected
          ? defaultOption.value
          : selectedCostCenterCode.selectedIds;
      }

      return params;
    }, [
      selectedSearchBy,
      selectedSearchByText,
      selectedFromId,
      selectedToId,
      selectedRegionCode.value,
      selectedEntityCode.value,
      selectedAreaCode.value,
      selectedCenterCode.value,
      selectedLocationCode.value,
      selectedCostCenterCode,
      costCenterOptions,
    ]);

    const handleSearchClick = useCallback(() => {
      const errors = validateForm();
      
      // Mark that search has been attempted
      if (!hasAttemptedSearch) {
        setHasAttemptedSearch(true);
      }

      if (errors.length > 0) {
        setValidationErrors(errors);
        window.scrollTo({ top: 0, behavior: "smooth" });
        return;
      }

      setValidationErrors([]);
      onSearch(buildSearchParams());
    }, [validateForm, buildSearchParams, onSearch, hasAttemptedSearch]);

    return (
      <div>
        {validationErrors.length > 0 && (
          <div className="mb-8!">
            <ErrorBanner errors={validationErrors} />
          </div>
        )}

        <div className="flex gap-x-8">
          <div className="w-1/2!">
            <Dropdown
              label="Search Criteria"
              selectedValue={selectedSearchBy}
              options={searchByOptions}
              onSelect={handleSearchBy}
              buttonClassName="w-full!"
              menuClassName="min-w-60 w-full!"
              popupClassName="-medium"
              popupTitle="Enter a wildcard search(must be at least 3 alpha-numeric characters), for example:"
              popupMessage="<ul class='list-disc! list-inside!'> <li>%Safety to find information that contains the word Safety somewhere in the field.</li> <li> Safety to find information that begins with the word Safety.</li></ul>"
            />
          </div>
          <div
            className="ds-form w-full!"
            data-ds-theme="vessel"
            data-ds-variant="input"
          >
            <label htmlFor="text-input">Search Value</label>
            <input
              ref={searchInputRef}
              autoComplete="on"
              className={`mt-1! w-1/2! ${validationErrors.some((e) => e.field === "searchText") ? "border-border-red! focus:outline-border-red! !" : ""}`}
              id="text-input"
              type="text"
              value={selectedSearchByText}
              onChange={(e) => handleSearchByTextChange(e.target.value)}
            />
            {validationErrors.some(
                (e) =>
                  e.field === "searchText"
              ) && (
              <ValidationErrorDisplay
                message={
                  validationErrors.find((e) => e.field === "searchText")?.message ??
                  ""
                }
                id="id-range-error-description-list-1"
              />
            )}
          </div>
        </div>

        {isUbtIdSearch && (
          <div className="flex gap-x-8 mt-8!">
            <div
              className="ds-form w-1/2!"
              data-ds-theme="vessel"
              data-ds-variant="input"
              ref={fromIdRef}
            >
              <label htmlFor="from-id-input">From ID</label>
              <input
                autoComplete="off"
                className={`mt-1! w-full! ${validationErrors.some((e) => e.field === "fromId" || e.field === "idRange") ? "border-border-red! focus:outline-border-red! !" : ""}`}
                id="from-id-input"
                type="text"
                inputMode="numeric"
                value={selectedFromId}
                onChange={(e) => handleFromIdChange(e.target.value)}
              />
              {isUbtIdSearch &&
                validationErrors.some(
                  (e) =>
                    e.field === "fromId" ||
                    e.field === "idRange",
                ) && (
                  <ValidationErrorDisplay
                    message={
                      validationErrors.find((e) => e.field === "idRange")?.message ??
                      validationErrors.find(
                        (e) => e.field === "fromId",
                      )?.message ??
                      ""
                    }
                    id="id-range-error-description-list-1"
                  />
              )}
            </div>
            <div
              className="ds-form w-1/2!"
              data-ds-theme="vessel"
              data-ds-variant="input"
            >
              <label htmlFor="to-id-input">To ID</label>
              <input
                autoComplete="off"
                className={`mt-1! w-full! ${validationErrors.some((e) => e.field === "toId" || e.field === "idRange") ? "border-border-red! focus:outline-border-red! !" : ""}`}
                id="to-id-input"
                type="text"
                inputMode="numeric"
                value={selectedToId}
                onChange={(e) => handleToIdChange(e.target.value)}
              />
              {isUbtIdSearch &&
                validationErrors.some(
                  (e) =>
                    e.field === "toId" ||
                    e.field === "idRange",
                ) && (
                  <ValidationErrorDisplay
                    message={
                      validationErrors.find((e) => e.field === "idRange")?.message ??
                      validationErrors.find(
                        (e) => e.field === "toId",
                      )?.message ??
                      ""
                    }
                    id="id-range-error-description-list-1"
                 />
              )}
            </div>
          </div>
        )}

        <div className="flex w-full! gap-x-8 mt-8! mb-8!">
          <div className="flex-1" ref={regionRef}>
            <Dropdown
              label="Region"
              selectedValue={selectedRegionCode}
              options={
                regionOptions.length ? regionOptions : [defaultRegionOption]
              }
              onSelect={handleRegion}
              menuClassName="w-full! max-h-60! overflow-y-auto!"
              buttonClassName={`w-full! ${validationErrors.some((e) => e.field === "region") ? "border-border-red!" : ""}`}
              placeholder="Select"
            />
            {validationErrors.some((e) => e.field === "region") && (
              <ValidationErrorDisplay
                message="Please select a region."
                id="region-dropdown-error-description-list-1"
              />
            )}
          </div>
          <div className="flex-1">
            <Dropdown
              label="Entity"
              selectedValue={selectedEntityCode}
              options={entityOptions.length ? entityOptions : [defaultOption]}
              onSelect={handleEntity}
              menuClassName="w-full! max-h-60! overflow-y-auto!"
              buttonClassName="w-full!"
            />
          </div>
        </div>

        <div className="flex w-full! gap-x-8 mb-8!">
          <div className="flex-1">
            <Dropdown
              label="Area"
              selectedValue={selectedAreaCode}
              options={areaOptions.length ? areaOptions : [defaultOption]}
              onSelect={handleArea}
              menuClassName="w-full! max-h-60! overflow-y-auto!"
              buttonClassName="w-full!"
            />
          </div>
          <div className="flex-1">
            <Dropdown
              label="Center"
              selectedValue={selectedCenterCode}
              options={centerOptions.length ? centerOptions : [defaultOption]}
              onSelect={handleCenter}
              menuClassName="w-full! max-h-60! overflow-y-auto!"
              buttonClassName="w-full!"
            />
          </div>
        </div>

        <div className="flex w-full! gap-x-8 mb-8!">
          <div className="w-1/2 pr-4!">
            <Dropdown
              label="Location"
              selectedValue={selectedLocationCode}
              options={
                locationOptions.length ? locationOptions : [defaultOption]
              }
              onSelect={handleLocation}
              menuClassName="w-full! max-h-60! overflow-y-auto!"
              buttonClassName="w-full!"
            />
          </div>
        </div>

        <div className="flex w-full! flex-col gap-x-8">
          <div className="w-[75%] pr-4!" ref={costCenterRef}>
            {!isLoadingData && (
              <>
                <MultiCheckboxSelect
                  label="Cost Center"
                  options={
                    costCenterOptions.length
                      ? costCenterOptions
                      : [defaultOption]
                  }
                  selectedValue={effectiveCostCenterIds}
                  onChange={handleCostCenterChange}
                  isSelectAll={selectedCostCenterCode.isAllSelected}
                  isSortBy
                  hideSelectAll={
                    !costCenterOptions.length ||
                    costCenterOptions[0]?.value === defaultOption.value
                  }
                  sortByOptions={[
                    { value: "id", label: "Department ID" },
                    { value: "name", label: "Name" },
                  ]}
                  sortByValue={sortByCostCenter}
                  onSortByChange={handleCostCenterSortBy}
                />
                {validationErrors.some((e) => e.field === "costCenter") && (
                  <ValidationErrorDisplay
                    message={`The max number of items from this list is 40. Your current selection contains ${selectedCostCenterCode.selectedIds.length}.`}
                    id="cost-center-error-description-list-1"
                  />
                )}
                {!validationErrors.some((e) => e.field === "costCenter") &&
                  !selectedCostCenterCode.isAllSelected &&
                  costCenterOptions.length > 40 && (
                    <div className="text-border-gray">
                      <ul id="cost-center-information-description-list-1">
                        <li className="flex gap-x-1">
                          <img
                            src="/assets/images/info-circle-small-solid.svg"
                            alt="Information"
                            width="14"
                            height="14"
                          />
                          <span className="screenreader-only">
                            The max number of items from this list is 40. Your
                            current selection contains{" "}
                            {selectedCostCenterCode.selectedIds.length}.
                          </span>{" "}
                          <p className="p4 m-0!">
                            The max number of items from this list is 40. Your
                            current selection contains{" "}
                            {selectedCostCenterCode.selectedIds.length}.
                          </p>
                        </li>
                      </ul>
                    </div>
                  )}
              </>
            )}
          </div>
        </div>
        <div className="flex gap-x-4 mt-8!">
          <button
            type="button"
            className="button py-0! -primary -light"
            data-analytics-type="button"
            data-analytics-click="Default"
            onClick={handleSearchClick}
          >
            Search
          </button>
        </div>
      </div>
    );
  },
);

export default UBTSearchContainer;
